import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { AIConversation } from '../../extViewmodels/sdk/models/AI';
import { useUserSession } from '../../models/hooks/appStateHooks';

const BASE_QUERY_KEY = 'sales-live-coaching-conversations';

/**
 * The server caps `pageSize` at 25 (and defaults to 25). We request that maximum so each page —
 * including each "Load more" fetch — pulls a full 25 conversations.
 */
const PAGE_SIZE = 25;

type QueryParams = {
	enabled?: boolean;
	refetchInterval?: number | false;
	impersonationContext?: Api.IImpersonationContext;
};

/**
 * Flattened view of the loaded pages, exposed as the query's `data` via react-query `select` so
 * consumers never reach into `data.pages` themselves. Pagination controls (`hasNextPage`,
 * `fetchNextPage`, `isFetchingNextPage`) stay on the top-level query result.
 */
export interface SalesLiveCoachingConversationsData {
	/** Conversations from every page loaded so far, in the server's order (newest-first). */
	conversations: AIConversation[];
	/** Total conversations matching the filter across ALL pages (from the most recent page response). */
	totalCount: number | null;
}

/**
 * Fetches `SalesLiveCoaching` AI conversations across the whole account, one page (max 25) at a time
 * via the cursor-paginated, admin-only `POST aiconversation/filter/account` endpoint. That route is
 * inherently account-wide (it applies an account-wide context server-side — there is no
 * `accountWide` request param) and is account-admin only, returning 403 to non-admins;
 * `ConversationList` surfaces that 403 gracefully. The original `aiconversation/filter` route is
 * unchanged (flat list, caller-scoped) and is intentionally NOT used here.
 *
 * Unlike `useTysonCoachingConversations`, which scopes results to the signed-in user via a
 * `CreatorId` criterion, this lists conversations created by ALL reps in the account.
 *
 * Returns a `useInfiniteQuery` result whose `data` is shaped by `select` into a flat
 * `{ conversations, totalCount }` ({@link SalesLiveCoachingConversationsData}); call
 * `fetchNextPage()` (gated on `hasNextPage`) to append the next page.
 */
export function useSalesLiveCoachingConversationsQuery(options?: QueryParams) {
	const userSession = useUserSession();
	const accountId = userSession.account?.id;

	const impersonationContext = options?.impersonationContext;
	const impersonationKey = impersonationContext
		? { accountId: impersonationContext.account?.id, userId: impersonationContext.user?.id }
		: undefined;

	const filter: Api.AIConversationFilter = {
		criteria: [
			{
				property: Api.AIConversationFilterProperty.ConversationType,
				value: Api.AIConversationType.SalesLiveCoaching,
			},
		],
		op: Api.FilterOperator.And,
	};

	return useInfiniteQuery({
		queryKey: [BASE_QUERY_KEY, accountId, impersonationKey],
		queryFn: ({ pageParam }) =>
			userSession.webServiceHelper.callAsync<Api.IPagedCollection<AIConversation>>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: 'aiconversation/filter/account',
					// `pageToken` is '' for the first page; `composeApiUrl` strips empty values, so no
					// `pageToken` query param is sent until we have a cursor from a prior page. Sort
					// defaults server-side to creationDate desc (the route's `[Sort]` attribute), mirroring
					// the `aiagenttemplate/filter` wiring — so we send only pageSize + pageToken.
					queryParams: {
						pageSize: PAGE_SIZE,
						pageToken: pageParam,
					},
					impersonationContext,
				}),
				'POST',
				filter
			),
		initialPageParam: '',
		// `pageToken` is the cursor for the NEXT page; null/absent means there are no more pages.
		// Coerce null -> undefined so react-query treats it as "no next page" and clears `hasNextPage`.
		getNextPageParam: lastPage => lastPage.pageToken ?? undefined,
		select: (data): SalesLiveCoachingConversationsData => ({
			conversations: data.pages.flatMap(page => page.values ?? []),
			totalCount: data.pages.at(-1)?.totalCount ?? null,
		}),
		enabled: options?.enabled ?? true,
		refetchInterval: options?.refetchInterval,
		meta: {
			toastErrorMessage: 'Failed to load coaching conversations',
			errorLogging: { fn: 'useSalesLiveCoachingConversationsQuery' },
		},
	});
}
